import { Box, Flex, Spacer, Stack, useMediaQuery } from '@chakra-ui/react';
import { ProductHighlight } from '@diamond/shared/types';
import { Button, IconButton, Text } from '@diamond/shared/ui';
import { trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import {
  filterVisibleHighlightProducts,
  getProductStockStatus,
  useShipToStore,
} from '@diamond/sol-buyer-context';
import { ArrowBackOutlined, ArrowForwardOutlined } from '@mui/icons-material';
import { useRouter } from 'next/router';
import type { Ref } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import shallow from 'zustand/shallow';

import { CardProduct } from '../common';

export interface ProductHomepageProps {
  data?: ProductHighlight[0];
  index: number;
  sectionRef?: Ref<HTMLDivElement>;
  onPageChange?: (startIndex: number) => void;
  onScrollContainerReady?: (container: HTMLDivElement | null) => void;
}
const SLIDES_TO_SHOW = 6;

const HIDE_SCROLLBAR_CSS = {
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none',
} as const;

export function ProductHomepage({
  data,
  index,
  sectionRef,
  onPageChange,
  onScrollContainerReady,
}: ProductHomepageProps) {
  const [isMobile] = useMediaQuery('(max-width: 992px)');
  const [selectedShipToId] = useShipToStore(
    (s) => [s.selectedShipToId],
    shallow
  );
  const [page, setPage] = useState(0);
  const { push } = useRouter();

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const productsData = useMemo(
    () => filterVisibleHighlightProducts(data?.products ?? []),
    [data?.products]
  );

  const handleClickProductCard = useCallback(
    (productId: string) => {
      push({
        pathname: `/product/${productId}`,
        query: { warehouse: selectedShipToId },
      });
    },
    [push, selectedShipToId]
  );

  const handleClickLihatSemua = useCallback(() => {
    const url = {
      pathname: '/highlights',
      query: { hid: data?.id, h_title: data?.title },
    };
    const section = `Product Highlight | ${data?.title}`;

    trackEvent(TRACKING_EVENTS.CLICK_PRODUCT_OTHERS, {
      page_name: TRACKING_PAGES.BERANDA,
      section_name: section,
      item_list_id: `Product Highlight | ${data?.id}`,
    });
    push(url, undefined, { shallow: true });
  }, [data?.id, data?.title, push]);

  const scrollByPixels = useCallback((px: number) => {
    const element = scrollContainerRef.current;
    if (!element) return;
    element.scrollBy({ left: px, behavior: 'smooth' });
  }, []);

  const assignScrollContainer = useCallback(
    (node: HTMLDivElement | null) => {
      scrollContainerRef.current = node;
      onScrollContainerReady?.(node);
    },
    [onScrollContainerReady]
  );

  const updatePage = useCallback(
    (updater: (prev: number) => number) => {
      setPage((prev) => {
        const next = updater(prev);
        onPageChange?.(next);
        return next;
      });
    },
    [onPageChange]
  );

  useEffect(() => {
    onPageChange?.(0);
  }, [onPageChange]);

  useEffect(() => {
    if (page >= productsData.length) {
      setPage(0);
      onPageChange?.(0);
    }
  }, [page, productsData.length, onPageChange]);
  const hasProducts = productsData.length > 0;

  if (!hasProducts) return null;

  return (
    <Box ref={sectionRef}>
      <Flex>
        <Box>
          <Text as="span" fontSize={{ base: '22px', md: '34px' }} mr="4">
            {data?.title}
          </Text>
          <Button
            variant="link"
            display={{ base: 'none', md: 'contents' }}
            color="blue"
            onClick={handleClickLihatSemua}
          >
            Lihat Semua
          </Button>
        </Box>
        <Spacer />
        <Button
          variant="link"
          display={{ base: 'contents', md: 'none' }}
          color="blue"
          onClick={handleClickLihatSemua}
        >
          Lihat Semua
        </Button>
        <Stack
          spacing="4"
          direction="row"
          display={{ base: 'none', lg: 'contents' }}
        >
          <IconButton
            aria-label="back-icon"
            name="back-icon"
            icon={<ArrowBackOutlined fontSize="small" />}
            variant="outline"
            borderWidth="1px"
            borderColor="blue"
            color="blue"
            isDisabled={page === 0}
            onClick={() => {
              if (page > 0) {
                scrollByPixels(-320);
                updatePage((prev) => Math.max(prev - SLIDES_TO_SHOW, 0));
              }
            }}
          />
          <IconButton
            aria-label="next-icon"
            name="next-icon"
            icon={<ArrowForwardOutlined fontSize="small" />}
            variant="outline"
            borderWidth="1px"
            borderColor="blue"
            color="blue"
            isDisabled={page >= productsData.length - SLIDES_TO_SHOW}
            onClick={() => {
              if (page < productsData.length - SLIDES_TO_SHOW) {
                scrollByPixels(320);
                updatePage((prev) =>
                  Math.min(
                    prev + SLIDES_TO_SHOW,
                    Math.max(productsData.length - SLIDES_TO_SHOW, 0)
                  )
                );
              }
            }}
          />
        </Stack>
      </Flex>
      <Box id={`container-highlight-${index}`}>
        {isMobile ? (
          <Stack
            direction="row"
            spacing={{ base: 4, md: 4 }}
            w="full"
            px={0}
            pb="6"
            mt={{ base: 4, md: 0 }}
            overflow="auto"
            css={HIDE_SCROLLBAR_CSS}
            ref={assignScrollContainer}
          >
            {productsData
              .map((val, idx) => {
                const price = val.prices?.[0]?.price ?? 0;
                const stockStatus = getProductStockStatus(val);
                return (
                  <Box key={idx} flex="none" width="var(--card-width-carousel)">
                    <CardProduct
                      productId={val.id}
                      imageUrl={val.product_images_medium?.[0]}
                      productName={val.brand_desc}
                      itemCode={val.item_code}
                      price={price}
                      unit={val.pack_unit}
                      description={val.title}
                      measurement_unit={val.sales_unit}
                      name="product"
                      navigateTo={{
                        pathname: `/product/${val.id}`,
                        query: { warehouse: selectedShipToId },
                      }}
                      onClickTambahKeranjang={() =>
                        handleClickProductCard(val.id)
                      }
                      stockStatus={stockStatus}
                      product={val}
                      section={`Product Highlight | ${data?.title}`}
                      sectionId={data?.id}
                      page={TRACKING_PAGES.BERANDA}
                    />
                  </Box>
                );
              })
              .slice(page, page + 10)}
          </Stack>
        ) : (
          <Box
            display="grid"
            gridTemplateColumns={`repeat(${SLIDES_TO_SHOW}, 1fr)`}
            gap="2"
            overflow="auto"
            pb="4"
            css={HIDE_SCROLLBAR_CSS}
            ref={assignScrollContainer}
          >
            {productsData
              .map((val, idx) => {
                const price = val.prices?.[0]?.price ?? 0;
                const stockStatus = getProductStockStatus(val);
                return (
                  <Box key={idx} flexBasis={{ base: 'auto', md: '25%' }}>
                    <CardProduct
                      productId={val.id}
                      imageUrl={val.product_images_medium?.[0]}
                      productName={val.brand_desc}
                      itemCode={val.item_code}
                      price={price}
                      unit={val.pack_unit}
                      description={val.title}
                      measurement_unit={val.sales_unit}
                      name="product"
                      navigateTo={{
                        pathname: `/product/${val.id}`,
                        query: { warehouse: selectedShipToId },
                      }}
                      onClickTambahKeranjang={() =>
                        handleClickProductCard(val.id)
                      }
                      stockStatus={stockStatus}
                      stock={val.stock}
                      product={val}
                      section={`Product Highlight | ${data?.title}`}
                      sectionId={data?.id}
                      page={TRACKING_PAGES.BERANDA}
                    />
                  </Box>
                );
              })
              .slice(page, page + SLIDES_TO_SHOW)}
          </Box>
        )}
      </Box>
    </Box>
  );
}
